/* WP-10 My Humidor collection (§14.5.2): brand groups of selectable box
 * tiles, loose cigars sheet, sticky selection bar / desktop panel. */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { portalApi } from '../api/portal-api';
import type { HumidorResponse, PortalBox, PortalLooseLine } from '../api/types';
import { EmptyState, PrimaryButton, SectionHeader, TextLink } from '../components/primitives';
import { BoxRow, BrandGroupHeader, Stepper } from '../components/tiles';
import { BottomSheet } from '../components/sheet';
import { EmptyStateIllustration, LooseCigarsIllustration } from '../illustrations';
import {
  draftCount,
  getPortalOwnerScope,
  loadDraft,
  saveDraft,
  type CollectDraft,
} from '../utils/selection';
import { errorText } from '../utils/error-text';
import { formatDateTime } from '../utils/dates';
import { currentPortalLocale } from '../i18n';
import { cn } from '../utils/cn';

const HINT_KEY = 'hp.humidorHintSeen';

/* UX25-02 — explicit inventory states: loading never claims zero stock or
 * fresh data, an initial failure replaces the page with an honest error +
 * retry, and a failed REFRESH keeps the last confirmed payload with a
 * warning and its own (still accurate) freshness label.
 * FIN-01 — every state is tagged with the owner scope that produced it;
 * the render path only shows a state whose tag equals the CURRENT scope,
 * so an identity change can never leave a previous owner's rows visible. */
type HumidorState =
  | { status: 'loading'; scope: string | null }
  | {
      status: 'ready';
      scope: string | null;
      data: HumidorResponse;
      loadedAt: string;
      refreshError: string | null;
    }
  | { status: 'error'; scope: string | null; message: string };

export function HumidorPage() {
  const { t } = useTranslation();
  const locale = currentPortalLocale();
  const navigate = useNavigate();
  const [state, setState] = useState<HumidorState>({ status: 'loading', scope: null });
  const stateRef = useRef(state);
  stateRef.current = state;
  const [refreshing, setRefreshing] = useState(false);
  const [draft, setDraft] = useState<CollectDraft>(() => loadDraft());
  const [hintSeen, setHintSeen] = useState(() => {
    try {
      return Number(localStorage.getItem(HINT_KEY) ?? '0') >= 3;
    } catch {
      return false;
    }
  });
  const [looseOpen, setLooseOpen] = useState(false);
  const [infoBox, setInfoBox] = useState<PortalBox | null>(null);
  const [brandFilter, setBrandFilter] = useState<string | null>(null);
  const [sortYear, setSortYear] = useState(false);
  const [query, setQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<PortalBox['status'] | 'ALL'>('ALL');

  /* UX25-02 — `humidorSeq` drops obsolete responses (newer request started
   * or the page unmounted/StrictMode-cleaned), `inflight` blocks overlapping
   * retries, `mountedRef` prevents state writes after unmount. Same shape as
   * the F3-B Home loader. */
  const humidorSeq = useRef(0);
  const inflight = useRef(false);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const load = useCallback(async () => {
    if (inflight.current) return;
    inflight.current = true;
    const my = ++humidorSeq.current;
    // FIN-01: bind the request to the owner scope at issue time — a retained
    // view may only carry over when it belongs to the same scope.
    const scope = getPortalOwnerScope();
    const retained =
      stateRef.current.status === 'ready' && stateRef.current.scope === scope
        ? stateRef.current
        : null;
    if (retained) {
      setRefreshing(true);
      setState({ ...retained, refreshError: null });
    } else {
      setState({ status: 'loading', scope });
    }
    try {
      const h = await portalApi.get<HumidorResponse>('/humidor');
      if (my !== humidorSeq.current || !mountedRef.current) return;
      setState({
        status: 'ready',
        scope,
        data: h,
        loadedAt: new Date().toISOString(),
        refreshError: null,
      });
    } catch (err) {
      if (my !== humidorSeq.current || !mountedRef.current) return;
      const msg = errorText(err, t);
      setState((prev) =>
        prev.status === 'ready' && prev.scope === scope
          ? { ...prev, refreshError: msg }
          : { status: 'error', scope, message: msg },
      );
    } finally {
      inflight.current = false;
      if (my === humidorSeq.current && mountedRef.current) setRefreshing(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  // FIN-01: a state tagged to another owner scope renders as loading —
  // rows, counts, freshness and errors can never cross an identity change.
  const scopeNow = getPortalOwnerScope();
  const scoped: HumidorState =
    state.scope === scopeNow ? state : { status: 'loading', scope: scopeNow };
  const data = scoped.status === 'ready' ? scoped.data : null;
  const loadedAt = scoped.status === 'ready' ? scoped.loadedAt : null;
  const refreshError = scoped.status === 'ready' ? scoped.refreshError : null;
  const error = scoped.status === 'error' ? scoped.message : null;

  const updateDraft = (next: CollectDraft) => {
    setDraft(next);
    saveDraft(next);
  };

  const toggleBox = (box: PortalBox) => {
    const has = draft.boxRefs.includes(box.boxRef);
    updateDraft({
      ...draft,
      boxRefs: has ? draft.boxRefs.filter((r) => r !== box.boxRef) : [...draft.boxRefs, box.boxRef],
    });
  };

  const counts = draftCount(draft);
  /* UX25-06 — search + availability filters: text matches product/brand
   * names, the status chips narrow by pickup availability. Filtering
   * never touches the persisted selection draft. */
  const q = query.trim().toLowerCase();
  const looseFiltered = useMemo(() => {
    const list = data?.loose ?? [];
    if (!q) return list;
    return list.filter((l) => `${l.name} ${l.brand}`.toLowerCase().includes(q));
  }, [data, q]);
  const brands = useMemo(() => {
    let list = data?.brands ?? [];
    if (brandFilter) list = list.filter((b) => (b.brandId ?? b.name) === brandFilter);
    list = list
      .map((b) => ({
        ...b,
        boxes: b.boxes.filter((box) => {
          if (statusFilter !== 'ALL' && box.status !== statusFilter) return false;
          if (q && !`${b.name} ${box.productName}`.toLowerCase().includes(q)) return false;
          return true;
        }),
      }))
      .filter((b) => b.boxes.length > 0);
    if (sortYear) {
      list = list.map((b) => ({
        ...b,
        boxes: [...b.boxes].sort((a, b2) => (b2.boxYear ?? 0) - (a.boxYear ?? 0)),
      }));
    }
    return list;
  }, [data, brandFilter, sortYear, statusFilter, q]);

  const totalBoxes = data?.totals.boxes ?? 0;

  const dismissHint = () => {
    setHintSeen(true);
    try {
      localStorage.setItem(HINT_KEY, '99');
    } catch {
      /* ignore */
    }
  };

  useEffect(() => {
    try {
      const n = Number(localStorage.getItem(HINT_KEY) ?? '0');
      if (n < 99) localStorage.setItem(HINT_KEY, String(n + 1));
    } catch {
      /* ignore */
    }
  }, []);

  return (
    <div className="hp-humidor-layout">
      <div className="hp-humidor-layout__stock">
        <div className="hp-row" style={{ gap: 16, marginTop: 8 }}>
          <div style={{ flex: 1 }}>
            <h1 className="hp-display-xl">{t('humidor.title')}</h1>
            {/* UX25-02 — freshness is a real timestamp: the server `asOf`
             * snapshot when present, otherwise the last successful client
             * retrieval — never a static "just now" claim. */}
            {data ? (
              <p className="hp-muted" data-quality-id="portal.humidor.freshness">
                {t('humidor.subtitle', { count: totalBoxes })} ·{' '}
                {data.asOf
                  ? t('humidor.stockAsOf', { time: formatDateTime(data.asOf, locale) })
                  : t('humidor.updatedAt', {
                      time: loadedAt ? formatDateTime(loadedAt, locale) : '—',
                    })}
              </p>
            ) : !error ? (
              <p className="hp-muted" role="status" aria-busy="true">
                {t('common.loading')}
              </p>
            ) : null}
          </div>
          {data ? (
            <button
              type="button"
              className="hp-btn hp-btn--secondary"
              onClick={() => void load()}
              disabled={refreshing}
              data-quality-id="portal.humidor.refresh"
            >
              {refreshing ? t('humidor.updating') : t('humidor.refresh')}
            </button>
          ) : null}
        </div>

        {error ? (
          <div>
            <p className="hp-danger-text" role="alert" data-quality-id="portal.humidor.error">
              {error}
            </p>
            <TextLink onClick={() => void load()} data-quality-id="portal.humidor.retry">
              {t('common.retry')}
            </TextLink>
          </div>
        ) : null}

        {refreshError ? (
          <div>
            <p
              className="hp-danger-text"
              role="alert"
              data-quality-id="portal.humidor.refreshError"
            >
              {t('humidor.refreshFailed')} — {refreshError}
            </p>
            <TextLink
              onClick={() => void load()}
              disabled={refreshing}
              data-quality-id="portal.humidor.retry"
            >
              {t('common.retry')}
            </TextLink>
          </div>
        ) : null}

        {refreshing && !refreshError ? (
          <p
            className="hp-field__hint"
            role="status"
            aria-busy="true"
            data-quality-id="portal.humidor.updating"
          >
            {t('humidor.updating')}
          </p>
        ) : null}

        {!hintSeen && data ? (
          <div className="hp-banner">
            <div className="hp-banner__body">{t('humidor.hint')}</div>
            <button type="button" className="hp-banner__dismiss" onClick={dismissHint}>
              {t('humidor.hintDismiss')}
            </button>
          </div>
        ) : null}

        {data && (totalBoxes > 0 || data.loose.length > 0) ? (
          <div style={{ marginTop: 16 }}>
            <label className="hp-field">
              <span className="hp-visually-hidden">{t('humidor.search')}</span>
              <input
                className="hp-input"
                type="search"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={t('humidor.searchPlaceholder')}
                data-quality-id="portal.humidor.search"
              />
            </label>
            <div
              className="hp-scroll-x"
              style={{ marginTop: 12 }}
              role="group"
              aria-label={t('humidor.filterStatus')}
            >
              {(
                [
                  ['ALL', t('humidor.filterAll')],
                  ['AVAILABLE', t('humidor.box.available')],
                  ['IN_COLLECTION', t('humidor.statusInCollection')],
                  ['ON_HOLD', t('humidor.box.onHold')],
                  ['IN_TRANSIT', t('humidor.box.inTransit')],
                ] as const
              ).map(([value, label]) => (
                <button
                  key={value}
                  type="button"
                  className={cn('hp-chip', statusFilter === value && 'hp-chip--on')}
                  aria-pressed={statusFilter === value}
                  onClick={() => setStatusFilter(value)}
                >
                  {label}
                </button>
              ))}
            </div>
            {/* UX25-02/06 — the status chips narrow boxes only; the member
             * must not read them as filtering loose stock too. */}
            <p className="hp-field__hint">{t('humidor.filterScope')}</p>
            {totalBoxes > 12 ? (
              <div className="hp-scroll-x" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  className={cn('hp-chip', !brandFilter && 'hp-chip--on')}
                  onClick={() => setBrandFilter(null)}
                >
                  {t('humidor.filterBrand')}
                </button>
                {data.brands.map((b) => (
                  <button
                    key={b.brandId ?? b.name}
                    type="button"
                    className={cn(
                      'hp-chip',
                      brandFilter === (b.brandId ?? b.name) && 'hp-chip--on',
                    )}
                    onClick={() => setBrandFilter(b.brandId ?? b.name)}
                  >
                    {b.name}
                  </button>
                ))}
                <button
                  type="button"
                  className={cn('hp-chip', sortYear && 'hp-chip--on')}
                  onClick={() => setSortYear((v) => !v)}
                >
                  {t('humidor.sortYear')}
                </button>
              </div>
            ) : null}
          </div>
        ) : null}

        {data && totalBoxes === 0 && data.loose.length === 0 ? (
          <EmptyState
            illustration={<EmptyStateIllustration title={t('humidor.empty.title')} />}
            sentence={t('humidor.empty.title')}
            action={<p className="hp-tertiary">{t('humidor.empty.body')}</p>}
          />
        ) : null}

        {data &&
        brands.length === 0 &&
        looseFiltered.length === 0 &&
        (totalBoxes > 0 || data.loose.length > 0) ? (
          <p className="hp-tertiary" role="status" data-quality-id="portal.humidor.noMatches">
            {t('humidor.noMatches')}
          </p>
        ) : null}

        {brands.map((brand) => (
          <section key={brand.brandId ?? brand.name} className="hp-brand">
            <BrandGroupHeader
              name={brand.name}
              countLabel={t('common.boxes', { count: brand.boxes.length })}
            />
            <div className="hp-rows">
              {brand.boxes.map((box) => (
                <BoxRow
                  key={box.boxRef}
                  box={box}
                  selected={draft.boxRefs.includes(box.boxRef)}
                  onToggle={() => toggleBox(box)}
                  onInfo={() => setInfoBox(box)}
                  statusLabel={boxStatusLabel(box, t)}
                  refLabel={t('common.boxRef', { ref: box.boxRef })}
                  ariaLabel={t('humidor.box.ariaLabel', {
                    name: box.productName,
                    year: box.boxYear ?? '—',
                    status: boxStatusLabel(box, t),
                    ref: box.boxRef,
                  })}
                />
              ))}
            </div>
          </section>
        ))}

        {data?.loose.length && looseFiltered.length ? (
          <section className="hp-section">
            <button type="button" className="hp-listrow" onClick={() => setLooseOpen(true)}>
              <LooseCigarsIllustration title={t('humidor.loose.title')} width={40} />
              <span>
                {t('humidor.loose.title')}
                <span className="hp-listrow__sub">
                  {t('humidor.loose.subtitle', {
                    count: looseFiltered.reduce((n, l) => n + l.sticks, 0),
                    brands: t('common.brands', {
                      count: new Set(looseFiltered.map((l) => l.brand)).size,
                    }),
                  })}
                </span>
              </span>
              <span className="hp-listrow__chev">›</span>
            </button>
          </section>
        ) : null}
      </div>

      <aside className="hp-selection-panel" aria-live="polite">
        <SelectionSummary counts={counts} onStart={() => navigate('/collect/new')} t={t} />
      </aside>

      {counts.boxes + counts.sticks > 0 ? (
        <div className="hp-selection-bar">
          <PrimaryButton
            onClick={() => navigate('/collect/new')}
            data-quality-id="portal.humidor.arrange"
          >
            {counts.sticks > 0
              ? t('humidor.selection.ctaWithCigars', {
                  boxes: t('common.boxes', { count: counts.boxes }),
                  cigars: t('common.cigars', { count: counts.sticks }),
                })
              : t('humidor.selection.cta', {
                  boxes: t('common.boxes', { count: counts.boxes }),
                })}
          </PrimaryButton>
        </div>
      ) : null}

      <BottomSheet
        open={looseOpen}
        onClose={() => setLooseOpen(false)}
        title={t('humidor.loose.title')}
      >
        <ul style={{ listStyle: 'none', padding: 0, margin: 0 }} className="hp-rows">
          {looseFiltered.map((line) => (
            <LooseRow
              key={line.productId}
              line={line}
              qty={draft.loose[line.productId] ?? 0}
              onChange={(qty) =>
                updateDraft({
                  ...draft,
                  loose: { ...draft.loose, [line.productId]: qty },
                })
              }
            />
          ))}
        </ul>
      </BottomSheet>

      <BottomSheet
        open={!!infoBox}
        onClose={() => setInfoBox(null)}
        title={infoBox?.productName ?? ''}
      >
        {infoBox ? (
          <div className="hp-stack">
            <p className="hp-display-l hp-num">{infoBox.boxYear ?? '—'}</p>
            <p className="hp-muted">
              {t('humidor.box.pack', { count: infoBox.sticksInBox })} · {boxStatusLabel(infoBox, t)}
            </p>
            {infoBox.collection ? (
              <TextLink onClick={() => navigate('/collect')}>
                {t('humidor.box.inCollection', { shop: infoBox.collection.requestNumber })}
              </TextLink>
            ) : null}
          </div>
        ) : null}
      </BottomSheet>
    </div>
  );
}

function boxStatusLabel(
  box: PortalBox,
  t: (k: string, o?: Record<string, unknown>) => string,
): string {
  switch (box.status) {
    case 'AVAILABLE':
      return t('humidor.box.available');
    case 'IN_COLLECTION':
      return t('humidor.box.inCollection', { shop: box.collection?.requestNumber ?? '' });
    case 'ON_HOLD':
      return t('humidor.box.onHold');
    case 'IN_TRANSIT':
      return t('humidor.box.inTransit');
  }
}

function LooseRow({
  line,
  qty,
  onChange,
}: {
  line: PortalLooseLine;
  qty: number;
  onChange: (qty: number) => void;
}) {
  return (
    <li className="hp-row hp-row--spread" style={{ padding: '12px 0' }}>
      <span>
        {line.name}
        <span className="hp-listrow__sub">
          {line.brand} · {line.availableSticks}/{line.sticks}
        </span>
      </span>
      <Stepper value={qty} max={line.availableSticks} onChange={onChange} />
    </li>
  );
}

function SelectionSummary({
  counts,
  onStart,
  t,
}: {
  counts: { boxes: number; sticks: number };
  onStart: () => void;
  t: (k: string, o?: Record<string, unknown>) => string;
}) {
  return (
    <div className="hp-stack">
      <SectionHeader title={t('collect.new.stepBoxes')} />
      {counts.boxes + counts.sticks === 0 ? (
        <p className="hp-tertiary">{t('humidor.selection.empty')}</p>
      ) : (
        <>
          <p>
            {t('common.boxes', { count: counts.boxes })}
            {counts.sticks > 0 ? ` · ${t('common.cigars', { count: counts.sticks })}` : ''}
          </p>
          <PrimaryButton onClick={onStart} data-quality-id="portal.humidor.arrangeAside">
            {counts.sticks > 0
              ? t('humidor.selection.ctaWithCigars', {
                  boxes: t('common.boxes', { count: counts.boxes }),
                  cigars: t('common.cigars', { count: counts.sticks }),
                })
              : t('humidor.selection.cta', {
                  boxes: t('common.boxes', { count: counts.boxes }),
                })}
          </PrimaryButton>
        </>
      )}
    </div>
  );
}
